/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, Input, computed, effect, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import template from './creative-set-analysis-page.component.html';
import {CreativeApiService} from '../creative/creative-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {deferSignalStateWrite} from '../common/util/template-state';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-set-analysis-page',
  template
})
export class CreativeSetAnalysisPageComponent {
  readonly creativeSet = signal<any>(null);
  readonly effectiveIsNew = signal(false);

  @Input()
  set model(model: any) {
    this.creativeSet.set(model);
  }

  @Input()
  set isNew(isNew: boolean) {
    this.effectiveIsNew.set(!!isNew);
  }

  readonly activeTab = signal<'charts' | 'creatives' | 'tags' | 'co2' | 'reports' | 'traffic' | 'location'>('charts');
  readonly showingTraffic = signal(false);
  readonly showingLocation = signal(false);
  readonly canSeeReports: boolean;

  readonly creatives = signal<any[]>([]);
  readonly creativesEmpty = computed(() => this.creatives().length === 0);
  readonly creativeCount = computed(() => this.creatives().length);
  readonly selCreative = signal<any>(null);
  readonly tagModel = computed(() => ({model: this.creativeSet()}));
  readonly showNewMessage = computed(() => this.effectiveIsNew());

  private loadRevision = 0;
  private readonly creativeLoadEffect = effect(() => {
    this.loadCreatives(this.creativeSet(), this.showNewMessage());
  });

  constructor(
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAllVisPermissions().AX_ADVERTISER;
  }

  selectTab(tab: ReturnType<CreativeSetAnalysisPageComponent['activeTab']>): void {
    this.activeTab.set(tab);
    if (tab === 'traffic') {
      this.showingTraffic.set(true);
    } else if (tab === 'location') {
      this.showingLocation.set(true);
    }
  }

  private loadCreatives(model: any, showNewMessage: boolean): void {
    const revision = ++this.loadRevision;
    if (!model?.id || showNewMessage) {
      this.creatives.set([]);
      this.selCreative.set(null);
      return;
    }
    firstValueFrom(this.Creative.listOptions(resourceOptionParams({creativeSet: model.id}))).then((data: any) => {
      deferSignalStateWrite(() => {
        if (revision !== this.loadRevision) {
          return;
        }
        const creatives = data.results || [];
        this.creatives.set(creatives);
        this.selCreative.set(creatives[0] || null);
      });
    });
  }

}
